import React from 'react';
import { Handle, Position } from 'reactflow';
import {
  Globe,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  HelpCircle,
} from 'lucide-react';
import { NodeData } from '../../../types';
import {
  RDAPStepConfig,
  RDAPLookupResponse,
} from '../../../types/rdap';
import { useFlowStore } from '../../../store';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import Tooltip from '../../common/Tooltip';
import { useDeleteStep } from '../../../hooks/queries/useStepMutations';

interface RDAPLookupNodeProps {
  data: NodeData;
  selected: boolean;
}

/**
 * Workflow node for the `rdap_lookup` step type (AC-1434 / AC-1440).
 *
 * Visualizes a Domain Availability step on the workflow canvas. Layout
 * mirrors `FilterNode` (the closest deterministic non-LLM analog per
 * Technical Design section 2.1) but uses the blue palette + Globe icon
 * called out in TD section 3.4 to keep RDAP visually distinct from
 * filter (rose), conditional (yellow), and queue write (teal).
 *
 * Last-run summary contract (AC-1439):
 *   The `unknown` count MUST be rendered with a visually distinct treatment
 *   from `available`. The PRD calls out the available-vs-unknown confusion
 *   as the canonical failure mode -- a workflow author should never look at
 *   a green "available" pill and unwittingly be reading domains the RDAP
 *   server failed to answer for. We surface unknown with an amber palette
 *   (matches the existing "warning" convention used by
 *   `filter-no-dedup-fields-warning`) and only render it when count > 0.
 *
 * Wiring (NOT this ticket): AC-1444 will register
 * `rdapLookup: RDAPLookupNode` in `WorkflowEditor.tsx`'s `nodeTypes` memo
 * and add `'rdap_lookup': 'rdapLookup'` to `AddStepButton.nodeTypeMap`.
 */
const RDAPLookupNode: React.FC<RDAPLookupNodeProps> = ({ data, selected }) => {
  const { nodes, edges, setNodes, setEdges } = useFlowStore();
  const [isDeletable, setIsDeletable] = React.useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  // Delete via React Query mutation hook (matches FilterNode / QueueWriteNode).
  const deleteStepMutation = useDeleteStep(data.step?.orchestration_id ?? '');

  // A step can be deleted only if no downstream step still points to it via
  // previous_step_id. Same guard FilterNode uses.
  React.useEffect(() => {
    if (!data.step || !data.step.orchestration_step_id) {
      setIsDeletable(false);
      return;
    }
    const canDelete = !nodes.some(
      (n) =>
        n.id !== data.id &&
        n.data?.step?.previous_step_id &&
        data.step?.orchestration_step_id &&
        String(n.data.step.previous_step_id) === String(data.step.orchestration_step_id),
    );
    setIsDeletable(canDelete);
  }, [nodes, data.id, data.step?.orchestration_step_id, data.step]);

  const handleDelete = async () => {
    if (!data.step?.orchestration_step_id) return;
    try {
      await deleteStepMutation.mutateAsync({
        stepId: data.step.orchestration_step_id,
      });
      setNodes(nodes.filter((node) => node.id !== data.id));
      setEdges(
        edges.filter((edge) => edge.source !== data.id && edge.target !== data.id),
      );
    } catch (error) {
      console.error('Failed to delete RDAP lookup step:', error);
    }
  };

  const getStatusIcon = () => {
    switch (data.status) {
      case 'running':
        return <Clock className="w-4 h-4 text-primary-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'running':
        return 'border-blue-500 bg-blue-50 dark:bg-blue-900/80';
      case 'completed':
        return 'border-green-500 bg-green-50 dark:bg-green-900/80';
      case 'failed':
        return 'border-red-500 bg-red-50 dark:bg-red-900/80';
      default:
        // Idle: blue palette per TD 3.4 ("Globe icon, blue palette").
        return 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-gray-800';
    }
  };

  // Pull config defensively. Older / partially-saved steps may omit fields.
  const cfg: Partial<RDAPStepConfig> =
    ((data.step as any)?.config as Partial<RDAPStepConfig>) || {};
  const inputSource = cfg.input_source || 'upstream_list';
  const sourceField = cfg.source_field || '';
  const literalDomains = Array.isArray(cfg.literal_domains)
    ? cfg.literal_domains
    : [];

  // Last-run aggregate counts come from the step executor's final_output
  // envelope (TD section 4.4). Counts are pre-computed by the backend so
  // we never recompute on the client.
  const lastRun: any = (data.step as any)?.last_run_result?.final_output;
  const lastRunResponse: Partial<RDAPLookupResponse> | null =
    lastRun &&
    typeof lastRun === 'object' &&
    typeof lastRun.total_checked === 'number'
      ? (lastRun as RDAPLookupResponse)
      : null;

  const showUnknown =
    !!lastRunResponse &&
    typeof lastRunResponse.unknown_count === 'number' &&
    lastRunResponse.unknown_count > 0;

  return (
    <div className="relative">
      <div
        className={`relative w-72 min-h-[8rem] px-4 py-3 rounded-lg border-2 shadow-lg transition-all duration-200
          ${selected ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}
          bg-gradient-to-br ${getStatusColor()}
          hover:shadow-xl hover:border-opacity-100`}
      >
        {isDeletable && (
          <button
            type="button"
            title="Delete step"
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteConfirm(true);
            }}
            className="absolute -top-2 -right-2 p-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors z-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}

        {/* Domain Availability badge -- blue palette + Globe icon (TD 3.4). */}
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-semibold tracking-wide shadow-md whitespace-nowrap z-20">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            <span>Domain Availability</span>
          </div>
        </div>

        {/* AC-1466: pt-3 (vs the original py-1) clears the first body chip
            below the protruding -top-3 "Domain Availability" pill so the
            source path / literal count metadata doesn't clip into it. */}
        <div className="flex flex-col items-center pt-3 pb-1 w-full">
          {getStatusIcon() && (
            <div className="flex items-center justify-center w-full mb-1">
              {getStatusIcon()}
            </div>
          )}

          {/* Input wiring summary: literal count vs. upstream field. */}
          {inputSource === 'literal' ? (
            <Tooltip content={`Literal domain list (${literalDomains.length})`}>
              <div className="px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs border border-blue-200 dark:border-blue-700 mx-2 self-stretch mb-1 truncate">
                {literalDomains.length === 0
                  ? 'No domains configured'
                  : `${literalDomains.length} domain${literalDomains.length === 1 ? '' : 's'}`}
              </div>
            </Tooltip>
          ) : sourceField ? (
            <Tooltip
              content={`Looking up domains from upstream field "${sourceField}"`}
            >
              <div className="px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs border border-blue-200 dark:border-blue-700 mx-2 self-stretch mb-1 font-mono truncate">
                {sourceField}
              </div>
            </Tooltip>
          ) : (
            <div className="text-xs italic text-gray-500 dark:text-gray-400 mb-1">
              No source field set
            </div>
          )}

          {/* Last-run summary. The PRD-flagged unknown-vs-available
              confusion is mitigated by:
                1. distinct test-id'd elements per bucket, and
                2. an amber palette + question-mark icon for unknown that
                   does not appear anywhere else in the badge row. */}
          {lastRunResponse && (
            <div
              className="flex items-center justify-center gap-1.5 flex-wrap"
              data-testid="rdap-last-run-summary"
              title={`Checked ${lastRunResponse.total_checked}: ${lastRunResponse.available_count ?? 0} available, ${lastRunResponse.taken_count ?? 0} taken${showUnknown ? `, ${lastRunResponse.unknown_count} unknown` : ''}`}
            >
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 font-medium"
                data-testid="rdap-count-total"
              >
                {lastRunResponse.total_checked} checked
              </span>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 font-medium"
                data-testid="rdap-count-available"
              >
                {lastRunResponse.available_count ?? 0} available
              </span>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-medium"
                data-testid="rdap-count-taken"
              >
                {lastRunResponse.taken_count ?? 0} taken
              </span>
              {showUnknown && (
                <Tooltip content="RDAP could not give a definitive answer for these domains. NOT the same as available -- inspect the per-domain reason in the run output.">
                  <span
                    className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 font-medium"
                    data-testid="rdap-count-unknown"
                  >
                    <HelpCircle className="w-2.5 h-2.5 flex-shrink-0" />
                    <span>{lastRunResponse.unknown_count} unknown</span>
                  </span>
                </Tooltip>
              )}
            </div>
          )}
        </div>

        {data.status === 'running' && (
          <div className="absolute inset-0 rounded-lg border-2 border-blue-500 animate-pulse" />
        )}

        {/* Both target and source handles on every cardinal direction so
            selectEdgeHandles (layoutEngine.ts) can bind incoming edges
            regardless of which side the upstream sits on. AgentNode follows
            the same pattern. AC-1455: a target-only declaration on
            top/left + source-only on right/bottom caused
            "Couldn't create edge for target handle id: 'right'" whenever
            the layout picked horizontal-dominant routing, which spammed the
            console on every render and made the canvas drag choppy. */}
        <Handle type="target" position={Position.Top} id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="source" position={Position.Top} id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="target" position={Position.Left} id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="source" position={Position.Left} id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="target" position={Position.Right} id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="source" position={Position.Right} id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="target" position={Position.Bottom} id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="source" position={Position.Bottom} id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
      </div>

      {showDeleteConfirm && (
        <ConfirmationDialog
          title="Delete Domain Availability Step"
          message="Are you sure you want to delete this step? This action cannot be undone."
          confirmText="Delete"
          confirmVariant="danger"
          onConfirm={() => {
            handleDelete();
            setShowDeleteConfirm(false);
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
};

export default RDAPLookupNode;
